import { createWebHistory, createRouter, type RouteLocation } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'vue-toastification'
import { userHasAnyRole } from '@/composables/roles'
import { requireRegistrationOpen } from '@/composables/platformBilling'
import { SUBSCRIPTION_TAB } from '@/lib/platformBilling'
import type { User } from '@/types/User'

const globalToast = useToast()

// Lazy load components
const IndexLayout = () => import('@/views/Index.vue')
const Login = () => import('@/views/Login.vue')
const Register = () => import('@/views/Register.vue')
const ForgottenPassword = () => import('@/views/ForgottenPassword.vue')
const ResetPassword = () => import('@/views/ResetPassword.vue')
const SocialCallback = () => import('@/views/SocialCallback.vue')
const Dashboard = () => import('@/views/App/Dashboard.vue')
const UsersTable = () => import('@/views/App/Tables/UsersTable.vue')
const ProductsTable = () => import('@/views/App/Tables/ProductsTable.vue')
const Kiosk = () => import('@/views/App/Kiosk.vue')
const Scoreboard = () => import('@/views/App/Scoreboard.vue')
const ScoreboardDual = () => import('@/views/App/ScoreboardDual.vue')
const MyProfile = () => import('@/views/App/Profiles/MyProfile.vue')
const AddUser = () => import('@/views/App/AddUser.vue')
const AddProduct = () => import('@/views/App/AddProduct.vue')
const GymProfile = () => import('@/views/App/Profiles/GymProfile.vue')
const UserProfile = () => import('@/views/App/Profiles/UserProfile.vue')
const Settings = () => import('@/views/App/Settings.vue')
const Billing = () => import('@/views/App/Billing.vue')
const StaffTable = () => import('@/views/App/Staff/StaffTable.vue')
const StaffProfile = () => import('@/views/App/Staff/StaffProfile.vue')
const Coverage = () => import('@/views/App/Staff/Coverage.vue')
const MySchedule = () => import('@/views/App/MySchedule.vue')
const Classes = () => import('@/views/App/Classes.vue')
const Groups = () => import('@/views/App/Groups.vue')
const Gradings = () => import('@/views/App/Gradings.vue')
const MakeASale = () => import('@/views/App/Shop/MakeASale.vue')
const ProductProfile = () => import('@/views/App/Shop/ProductProfile.vue')

// Function to check user roles
function hasRole(user: User, requiredRoles: string) {
    const roles = Array.isArray(requiredRoles) ? requiredRoles : requiredRoles.split('|')
    return user?.roles?.some((role) => roles.includes(role.name))
}

// Define routes
const routes = [
    {
        path: '/',
        component: Login,
        meta: { guest: true }
    },
    {
        path: '/login',
        component: Login,
        meta: { guest: true }
    },
    {
        path: '/register',
        component: Register,
        meta: { guest: true },
        // Sign-up is off unless the API turns it on. The guard sends the
        // visitor to sign-in when it is off, or when the check fails.
        beforeEnter: requireRegistrationOpen
    },
    {
        /*
         * Older addresses for the Manajits plan, which is now a tab of
         * Settings. Stripe sessions made before the move still send the
         * browser here, so the query (`?checkout=success`) is kept.
         */
        path: '/settings/subscription',
        redirect: (to: RouteLocation) => ({
            path: '/app/settings',
            query: { ...to.query, tab: SUBSCRIPTION_TAB }
        })
    },
    {
        path: '/app/settings/subscription',
        redirect: (to: RouteLocation) => ({
            path: '/app/settings',
            query: { ...to.query, tab: SUBSCRIPTION_TAB }
        })
    },
    {
        path: '/forgotten-password',
        component: ForgottenPassword,
        meta: { guest: true }
    },
    {
        /*
         * Where the reset email lands: FRONTEND_URL + this path, built by the
         * API's AuthServiceProvider. Token and email arrive in the query.
         *
         * Deliberately not `guest`, for the same reason as the social callback
         * below: the store persists the last user, so `guest` would bounce
         * anyone whose browser remembers an old session to the dashboard, and
         * from there to sign-in, before the link could be used. The token is
         * what authorises a reset, not who appears to be signed in.
         */
        path: '/reset-password',
        component: ResetPassword
    },
    {
        /*
         * Where the API returns the browser after a social sign-in. Must match
         * SOCIALITE_SPA_REDIRECT on the API, which defaults to FRONTEND_URL +
         * this path. Without it a successful sign-in ends in a 404.
         *
         * Deliberately neither `guest` nor `requiresAuth`. `guest` bounces a
         * visitor to the dashboard whenever a user is present in the store —
         * and the store persists one — so a member returning with an error
         * reason would be redirected before this page could read it, then sent
         * back to login by the interceptor with nothing explaining why.
         */
        path: '/auth/social/callback',
        component: SocialCallback
    },
    {
        path: '/app',
        component: IndexLayout,
        meta: { requiresAuth: true },
        children: [
            {
                path: 'dashboard',
                component: Dashboard,
                meta: { requiresAuth: true }
            },
            {
                path: 'users',
                component: UsersTable,
                meta: { requiresAuth: true }
            },
            {
                /*
                 * Instructors run gradings, so they read the board and award
                 * stripes; a belt still goes through the member's own record.
                 */
                path: 'gradings',
                name: 'Gradings',
                component: Gradings,
                meta: { requiresAuth: true, roles: ['admin', 'manager', 'instructor'] }
            },
            {
                /*
                 * The till. Instructors can ring up a sale — the counter is
                 * often staffed by whoever is teaching — but not refund one.
                 */
                path: 'shop/sale',
                name: 'MakeASale',
                component: MakeASale,
                meta: { requiresAuth: true, roles: ['admin', 'manager', 'instructor'] }
            },
            {
                path: 'shop-inventory',
                component: ProductsTable,
                meta: { requiresAuth: true }
            },
            {
                path: 'shop-inventory/add',
                component: AddProduct,
                meta: { requiresAuth: true }
            },
            {
                /*
                 * One product: edit it, adjust its stock, read its history.
                 * Open to the same roles as the table that leads here, so the
                 * row action never dead-ends again. The page itself hides the
                 * history from non-staff and the editing from non-managers,
                 * mirroring the API's gates.
                 */
                path: 'shop-inventory/:id',
                name: 'ProductProfile',
                component: ProductProfile,
                meta: { requiresAuth: true }
            },
            {
                path: 'my-profile',
                name: 'MyProfile',
                component: MyProfile,
                meta: { requiresAuth: true }
            },
            {
                path: 'users/add',
                component: AddUser,
                meta: { requiresAuth: true }
            },
            {
                path: 'gym-profile',
                component: GymProfile,
                meta: { requiresAuth: true }
            },
            {
                path: 'users/:id/profile',
                component: UserProfile,
                meta: { requiresAuth: true }
            },
            {
                path: 'settings',
                component: Settings,
                meta: { requiresAuth: true } // Only accessible by manager
            },
            {
                path: 'billing',
                component: Billing,
                // Every billing endpoint is admin-or-manager. An instructor or
                // student reaching this by URL would collect a 403 per panel,
                // so the guard sends them back instead.
                meta: { requiresAuth: true, roles: ['admin', 'manager'] }
            },
            {
                path: 'staff',
                component: StaffTable,
                // The roster and everything under it is admin-or-manager on the
                // API, same as billing.
                meta: { requiresAuth: true, roles: ['admin', 'manager'] }
            },
            {
                // Before the `:id` route so the word is not read as a staff id.
                path: 'staff/coverage',
                component: Coverage,
                meta: { requiresAuth: true, roles: ['admin', 'manager'] }
            },
            {
                path: 'staff/:id',
                component: StaffProfile,
                meta: { requiresAuth: true, roles: ['admin', 'manager'] }
            },
            {
                /*
                 * Gated, where Gym Profile was not. These screens create and
                 * delete schedules, so they are gym management rather than
                 * reference — see the navigation restructure spec.
                 */
                path: 'classes',
                component: Classes,
                meta: { requiresAuth: true, roles: ['admin', 'manager'] }
            },
            {
                path: 'groups',
                component: Groups,
                meta: { requiresAuth: true, roles: ['admin', 'manager'] }
            },
            {
                path: 'my-schedule',
                /*
                 * Any staff member's own sessions. Read-only — Coverage is
                 * where someone else's week is changed.
                 *
                 * Open to admins and managers as well as instructors, because
                 * plenty of gym owners teach and hold only the admin role.
                 * Gating this on `instructor` alone would lock an owner out of
                 * their own timetable. It also matches the API, where
                 * GET /me/class-sessions allows admin|manager|instructor and
                 * scopes the result to the caller — someone who teaches
                 * nothing simply sees an empty week.
                 */
                component: MySchedule,
                meta: { requiresAuth: true, roles: ['admin', 'manager', 'instructor'] }
            }
        ]
    },
    {
        path: '/app/kiosk',
        component: Kiosk,
        meta: { requiresAuth: true }
    },
    {
        path: '/app/scoreboard',
        component: Scoreboard,
        meta: { requiresAuth: true }
    },
    {
        path: '/app/scoreboard-dual',
        component: ScoreboardDual,
        meta: { requiresAuth: true }
    },
    // Dev-only living style guide for the token system. Tree-shaken out of
    // production builds, since import.meta.env.DEV is statically false there.
    ...(import.meta.env.DEV
        ? [
              {
                  path: '/dev/style-guide',
                  component: () => import('@/views/Dev/StyleGuide.vue'),
                  meta: { guest: true }
              }
          ]
        : []),
    {
        path: '/app/:pathMatch(.*)*',
        redirect: '/app/dashboard'
    }
]

// Create router instance
const router = createRouter({
    history: createWebHistory(),
    routes
})

// Global route guard
router.beforeEach(async (to) => {
    const authStore = useAuthStore()

    if (to.matched.some((record) => record.meta.requiresAuth)) {
        // Optimistic check only. The session cookie is the real authority, and
        // the response interceptor redirects if the API rejects it — the guard
        // cannot see an HttpOnly cookie to test.
        if (!authStore.user) {
            globalToast.warning('You are unauthorized. Please log in!')
            return '/login' // Redirect to login page if not authenticated
        }

        // Handle kiosk mode logic here if needed...

        const kioskActive = authStore.user?.settings?.some(
            (setting) => setting.key === 'kiosk_active' && setting.value == '1'
        )

        if (kioskActive && to.path !== '/app/kiosk') {
            globalToast.warning(
                'The Kiosk mode is currently active. Please exit to resume use of Manajits.'
            )
            return '/app/kiosk' // Redirect to kiosk if active
        }
    } else if (to.matched.some((record) => record.meta.guest)) {
        // If already logged in and trying to access guest routes, redirect to dashboard.
        if (authStore.user) {
            return '/app/dashboard'
        }
    }

    /*
     * Role-based access control.
     *
     * `meta.roles` is the general form: a list, so "admin or manager" can be
     * expressed. The three single-role flags below predate it and are unused,
     * but are left alone rather than removed as a side effect of this change.
     */
    const requiredRoles = to.matched
        .flatMap((record) => (record.meta.roles as string[] | undefined) ?? [])
        .filter(Boolean)

    if (requiredRoles.length && authStore.user && !userHasAnyRole(authStore.user, requiredRoles)) {
        globalToast.warning('You do not have the correct permissions to access this resource!')
        return '/app/dashboard'
    }

    if (to.matched.some((record) => record.meta.adminAuth)) {
        if (authStore.user && !hasRole(authStore?.user, 'admin')) {
            globalToast.warning('You do not have the correct permissions to access this resource!')
            return '/app/dashboard'
        }
    }

    if (to.matched.some((record) => record.meta.managerAuth)) {
        if (authStore.user && !hasRole(authStore?.user, 'manager')) {
            globalToast.warning('You do not have the correct permissions to access this resource!')
            return '/app/dashboard'
        }
    }

    if (to.matched.some((record) => record.meta.instructorAuth)) {
        if (authStore.user && !hasRole(authStore?.user, 'instructor')) {
            globalToast.warning('You do not have the correct permissions to access this resource!')
            return '/app/dashboard'
        }
    }

    return true // Allow navigation for all other cases
})

export default router
